import { Editor } from "@monaco-editor/react";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { processLiquidTemplate } from "../services/integrations/process-liquid-template";
import { QueryActionFormInputs } from "../types/form-inputs";
import { ErrorMessage } from "./catalyst/ErrorMessage";
import { Field } from "./catalyst/Field";
import { Label } from "./catalyst/Label";
import EntityAwareIntellisenseEditor from "./json/EntityAwareIntellisenseEditor";

export const QueryActionPayloadField = ({
  editorFormat,
}: {
  editorFormat: "json" | "liquid";
}): JSX.Element => {
  const {
    control,
    formState: { errors },
    setError,
    watch,
  } = useFormContext<QueryActionFormInputs>();
  const httpMethod = watch("method");
  const payloadValue = watch("payload");

  const [liquidPreview, setLiquidPreview] = useState<string>("");

  const handleProcessTemplate = processLiquidTemplate(
    payloadValue,
    (value) => {
      setLiquidPreview(value);
    },
    (errorMessage) => {
      setError("payload", {
        message: errorMessage,
      });
    },
  );

  useEffect(() => {
    if (editorFormat === "liquid") {
      handleProcessTemplate();
    }
  }, [payloadValue, editorFormat]);

  return (
    <div>
      <Field>
        <div className="h-[200px] px-2 py-2 sm:p-1 dark:text-gray-500">
          <Controller
            control={control}
            defaultValue=""
            name="payload"
            render={({ field: { name, onChange, value } }) => (
              <EntityAwareIntellisenseEditor
                fieldName={name}
                language={editorFormat}
                onChange={(value) => onChange(value || "")}
                value={value}
              />
            )}
            rules={{
              validate: (value) => {
                if (httpMethod === "GET") {
                  if (value) {
                    return "Payload is not allowed for GET";
                  }
                }
                if (["POST", "PUT"].includes(httpMethod)) {
                  if (!value) {
                    return "Payload is required for POST and PUT";
                  } else {
                    const jsonString =
                      editorFormat === "liquid" ? liquidPreview : value;
                    try {
                      JSON.parse(jsonString);
                      return true;
                    } catch (error) {
                      return `Invalid JSON ${error}`;
                    }
                  }
                }
                return true;
              },
            }}
          />
        </div>
        {errors?.payload && (
          <ErrorMessage>{errors.payload.message}</ErrorMessage>
        )}
      </Field>
      {editorFormat === "liquid" && liquidPreview !== "" && (
        <div className="mt-4">
          <Field>
            <Label>Preview</Label>
            <div className="mt-2">
              <Editor
                className="monaco-bordered"
                height="200px"
                language="json"
                options={{
                  automaticLayout: true,
                  glyphMargin: false,
                  lineNumbers: "off",
                  lineNumbersMinChars: 2,
                  links: false,
                  minimap: { enabled: false },
                  padding: { bottom: 4, top: 4 },
                  //readOnly: true,       // don't get json validation if its in readonly mode
                  scrollbar: {
                    horizontal: "hidden",
                    vertical: "auto",
                  },
                  wordWrap: "off",
                }}
                theme="miru-dark"
                value={liquidPreview}
                width="100%"
              />
            </div>
          </Field>
        </div>
      )}
    </div>
  );
};
